import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Button,
} from "./TailwindMuiStubs";
import { Download as DownloadIcon } from "lucide-react";
import socket, { connectSocket, disconnectSocket } from "../../socket";

const DataExport = () => {
    const [crews, setCrews] = useState([]);
    const { selectedYear } = useOutletContext();

    useEffect(() => {
        connectSocket();

        const fetchCrews = () => {
            socket.emit("fetchCrews", selectedYear);
        };

        fetchCrews();

        socket.on("crewsData", (data) => {
            setCrews(data);
        });

        socket.on("crewUpdated", (updatedCrew) => {
            setCrews((prevCrews) =>
                prevCrews.map((crew) => (crew.id === updatedCrew.id ? updatedCrew : crew))
            );
        });

        return () => {
            disconnectSocket();
            socket.off("crewsData");
            socket.off("crewUpdated");
        };
    }, [selectedYear]);

    const handleExportCSV = () => {
        // Headers
        const headers = [
            "Štartovné číslo",
            "Kategória",
            "Mená",
            "Zaplatené",
            "Čas štartu",
            "Čas cieľa",
            "Výsledný čas"
        ];

        // Format rows
        const rows = crews.map((crew) => {
            const startNumber = crew.start_number || "";
            const category = crew.category?.name || "";
            
            // Join names with newline and wrap in quotes for Excel to interpret as multi-line cell
            const names = crew.participants && crew.participants.length > 0 
                ? `"${crew.participants.map(p => p.name).join("\n")}"` 
                : "";
                
            const paid = crew.invoice?.paid ? "Áno" : "Nie";
            const startTime = crew.race_time?.start_time || "";
            const finishTime = crew.race_time?.finish_time || "";
            const totalTime = crew.race_time?.total_time || "";

            return [
                startNumber,
                category,
                names,
                paid,
                startTime,
                finishTime,
                totalTime
            ].join(";"); // Using semicolon for standard European Excel CSV
        });

        // Add UTF-8 BOM so Excel opens accents correctly
        const csvContent = "\uFEFF" + [headers.join(";"), ...rows].join("\r\n");
        
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `maraton_export_${selectedYear}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="space-y-8">
            <div className="mb-8 flex justify-between items-end">
                <div>
                    <Typography variant="h4">Export dát</Typography>
                    <Typography variant="body2">
                        Prehľad všetkých posádok a ich údajov pre ročník {selectedYear}. Dáta si môžete stiahnuť do Excelu.
                    </Typography>
                </div>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleExportCSV}
                    style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                >
                    <DownloadIcon size={18} />
                    Stiahnuť pre Excel (CSV)
                </Button>
            </div>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Štart. číslo</TableCell>
                            <TableCell>Kategória</TableCell>
                            <TableCell>Mená</TableCell>
                            <TableCell>Zaplatené</TableCell>
                            <TableCell>Čas štartu</TableCell>
                            <TableCell>Čas cieľa</TableCell>
                            <TableCell>Výsledný čas</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {crews.map((crew) => (
                            <TableRow key={crew.id}>
                                <TableCell>{crew.start_number || ""}</TableCell>
                                <TableCell>{crew.category?.name || ""}</TableCell>
                                <TableCell>
                                    {crew.participants?.map((p, idx) => (
                                        <div key={idx}>{p.name}</div>
                                    ))}
                                </TableCell>
                                <TableCell>
                                    <span className={`px-2 py-1 rounded text-xs font-bold ${crew.invoice?.paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                        {crew.invoice?.paid ? "Áno" : "Nie"}
                                    </span>
                                </TableCell>
                                <TableCell>{crew.race_time?.start_time || ""}</TableCell>
                                <TableCell>{crew.race_time?.finish_time || ""}</TableCell>
                                <TableCell>{crew.race_time?.total_time || ""}</TableCell>
                            </TableRow>
                        ))}
                        {crews.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>
                                    Žiadne dáta pre ročník {selectedYear}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default DataExport;
